import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { finalize, map, take } from 'rxjs';
import { ButtonComponent } from '@shared/components/button';
import { ModalService } from '@shared/components/modal';
import { FormFieldComponent } from '@shared/components/form-field';
import { IconComponent } from '@shared/components/icon';
import { InputComponent } from '@shared/components/input';
import { AlertComponent } from '@shared/components/alert';
import { SelectComponent } from '@shared/components/select';
import { SpinnerComponent } from '@shared/components/spinner';
import { StatusChipComponent, type StatusTone } from '@shared/components/status-chip';
import { ToggleComponent } from '@shared/components/toggle';
import { TooltipDirective } from '@shared/directives/tooltip';

import type { SelectOption } from '@shared/components/dropdown/dropdown.types';
import { RolesUtilisateurService } from '../../../../partage/components/login/roles-utilisateur.service';
import {
  EtatFilsDeLEau,
  EtatPartenaireFilDeLEau,
  LONGUEURS_MAXIMALES,
  SensSynchronisation,
} from '../liste-clients.interface';
import { ListeClientsService } from '../liste-clients.service';
import {
  DialogModifierBaseDeDonneesComponent,
  DialogModifierBaseDeDonneesData,
  DialogModifierBaseDeDonneesOutput,
} from './dialog-modifier-base-de-donnees/dialog-modifier-base-de-donnees.component';
import { DialogSensSynchronisationComponent } from './dialog-sens-synchronisation/dialog-sens-synchronisation.component';

@Component({
  selector: 'app-informations-client',
  imports: [
    AlertComponent,
    ButtonComponent,
    FormFieldComponent,
    IconComponent,
    InputComponent,
    SelectComponent,
    SpinnerComponent,
    StatusChipComponent,
    ToggleComponent,
    TooltipDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './informations-client.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './informations-client.component.scss',
})
export class InformationsClientComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  private listeClientsService = inject(ListeClientsService);
  private modal = inject(ModalService);

  // ServiceClient : options et fils de l'eau modifiables, label et base de données en
  // lecture seule ; connection string et synchronisation réservées à Tech et Admin
  accesComplet = inject(RolesUtilisateurService).accesClientsComplet;

  tenantSelectionne = this.listeClientsService.tenantSelectionne;
  detailClient = this.listeClientsService.detailClient;
  chargementDetailEnCours = this.listeClientsService.chargementDetailEnCours;

  chargementConnectionStringEnCours = signal<boolean>(false);

  // La base actuelle du client (Properties:DatabaseName) reste proposée même si elle
  // n'apparaît pas dans le registre Storage:Databases
  optionsBaseDeDonnees = computed(() => {
    const connectionStrings = this.listeClientsService.connectionStrings();
    const baseActuelle = this.tenantSelectionne()?.databaseName;
    if (baseActuelle && !connectionStrings.includes(baseActuelle)) {
      return [baseActuelle, ...connectionStrings];
    }
    return connectionStrings;
  });

  clientForm = new FormGroup({
    databaseName: new FormControl<string>('', { nonNullable: true }),
    label: new FormControl<string>('', { nonNullable: true }),
    managerIsEnabled: new FormControl<boolean>(false, { nonNullable: true }),
    meetchIsEnabled: new FormControl<boolean>(false, { nonNullable: true }),
    isOrange: new FormControl<boolean>(false, { nonNullable: true }),
    isSkidata: new FormControl<boolean>(false, { nonNullable: true }),
    isEventim: new FormControl<boolean>(false, { nonNullable: true }),
    disabled: new FormControl<boolean>(false, { nonNullable: true }),
    isActiveArchiving: new FormControl<boolean>(false, { nonNullable: true }),
    meetchApiKey: new FormControl<string>('', { nonNullable: true }),
  });

  // `app-select` raisonne en options et en tableau de clés, et n'implémente pas
  // ControlValueAccessor : ces membres font la jonction avec le FormControl.
  // Déclarés après `clientForm`, dont ils dépendent à l'initialisation
  protected optionsBases = computed<SelectOption[]>(() =>
    this.optionsBaseDeDonnees().map(base => ({ value: base, label: base })),
  );

  // Miroir signal de `databaseName`. Adossé à `valueChanges` et non à une écriture
  // manuelle : le contrôle est aussi alimenté par les `patchValue` du chargement
  // du client, qu'un miroir mis à jour à la main manquerait
  private baseDeDonneesCourante = toSignal(this.clientForm.controls.databaseName.valueChanges, {
    initialValue: this.clientForm.controls.databaseName.value,
  });

  // Même principe pour les valeurs que lit le gabarit (bascules, champ Meetch)
  protected readonly valeursClient = toSignal(
    this.clientForm.valueChanges.pipe(map(() => this.clientForm.getRawValue())),
    {
      initialValue: this.clientForm.getRawValue(),
    },
  );

  protected basesSelectionnees = computed<string[]>(() => {
    const base = this.baseDeDonneesCourante();
    return base ? [base] : [];
  });

  protected choisirBaseDeDonnees(valeurs: readonly string[]): void {
    this.clientForm.controls.databaseName.setValue(valeurs[0] ?? '');
  }

  constructor() {
    // Lecture seule pour les rôles sans accès complet (les contrôles désactivés gardent
    // leur valeur, qui repart telle quelle dans le payload du Valider)
    effect(() => {
      if (this.accesComplet()) {
        this.clientForm.controls.label.enable();
        this.clientForm.controls.databaseName.enable();
      } else {
        this.clientForm.controls.label.disable();
        this.clientForm.controls.databaseName.disable();
      }
    });

    // Pré-remplissage immédiat depuis la ligne de la liste, puis complété/écrasé par le
    // détail agrégé (tenant + fallback annuaire) dès qu'il est chargé. Seuls les champs
    // réellement portés par la ligne sont patchés : label, disabled et isActiveArchiving
    // n'existent que sur le détail — les réinitialiser ici les effacerait pour de bon si
    // le chargement du détail échoue (le formulaire partirait alors dans le Valider).
    // meetchApiKey, champ en écriture seule, se vide à chaque changement de sélection
    effect(() => {
      const tenant = this.tenantSelectionne();
      // Le diagnostic des fils de l'eau appartient au client affiché : il ne survit pas
      // à un changement de sélection
      this.etatFilsDeLEau.set(undefined);
      if (tenant) {
        this.clientForm.patchValue({
          databaseName: tenant.databaseName ?? '',
          managerIsEnabled: tenant.managerIsEnabled,
          meetchIsEnabled: tenant.meetchIsEnabled,
          isOrange: tenant.isOrange,
          isSkidata: tenant.isSkidata,
          isEventim: tenant.isEventim,
          meetchApiKey: '',
        });
      }
    });

    effect(() => {
      const detail = this.detailClient();
      if (detail) {
        this.clientForm.patchValue({
          databaseName: detail.databaseName ?? '',
          managerIsEnabled: detail.managerIsEnabled,
          meetchIsEnabled: detail.meetchIsEnabled,
          isOrange: detail.isOrange,
          isSkidata: detail.isSkidata,
          isEventim: detail.isEventim,
          label: detail.label ?? '',
          disabled: detail.disabled,
          isActiveArchiving: detail.isActiveArchiving,
          // Écriture seule : le backend ne renvoie jamais la clé, le champ reste vide et
          // n'est envoyé que si l'utilisateur saisit une nouvelle valeur
          meetchApiKey: '',
        });
      }
    });
  }

  // État du provisionnement des fils de l'eau, chargé à la demande uniquement : la
  // vérification ouvre une connexion vers la base Aparté du client, on ne la joue pas à
  // chaque sélection
  etatFilsDeLEau = signal<EtatFilsDeLEau | undefined>(undefined);
  chargementFilsDeLEauEnCours = signal<boolean>(false);
  reprovisionnementEnCours = signal<boolean>(false);

  // Le bouton de réparation n'a de sens que sur une divergence : un fil de l'eau ACTIF dont
  // la base n'est pas provisionnée, ou un socle incomplet alors qu'un fil de l'eau est actif.
  // Non souscrit et non provisionné est l'état normal, pas une divergence ; le prérequis
  // absent est hors de portée, aucun script ne le crée
  divergenceFilsDeLEau = computed(() => {
    const etat = this.etatFilsDeLEau();
    if (!etat || !etat.prerequisSatisfaits) {
      return false;
    }
    const actifs = etat.partenaires.filter(partenaire => partenaire.actif);
    return actifs.length > 0 && (!etat.socleProvisionne || actifs.some(partenaire => !partenaire.provisionne));
  });

  verifierFilsDeLEau() {
    const tenant = this.tenantSelectionne();
    if (!tenant || this.chargementFilsDeLEauEnCours()) {
      return;
    }

    this.chargementFilsDeLEauEnCours.set(true);
    this.listeClientsService
      .recupererEtatFilsDeLEau$(tenant.identifier)
      .pipe(
        take(1),
        finalize(() => this.chargementFilsDeLEauEnCours.set(false)),
      )
      .subscribe(etat => {
        // La réponse n'appartient qu'à la base interrogée : l'appel est lent (une connexion
        // vers la base du client), et une sélection qui a changé entre-temps ne doit pas
        // hériter du diagnostic du client précédent — jusqu'au bouton Reprovisionner qui
        // s'afficherait pour la mauvaise base
        if (this.tenantSelectionne()?.identifier !== tenant.identifier) {
          return;
        }
        this.etatFilsDeLEau.set(etat);
      });
  }

  // Réparation : rejoue la convergence pour les fils de l'eau déjà activés, puis relit l'état.
  // Le diagnostic affiché doit refléter la base APRÈS réparation, pas celui qui a motivé le clic
  reprovisionnerFilsDeLEau() {
    const tenant = this.tenantSelectionne();
    if (!tenant || this.reprovisionnementEnCours()) {
      return;
    }

    this.reprovisionnementEnCours.set(true);
    this.listeClientsService
      .reprovisionnerFilsDeLEau$({ identifiant: tenant.identifier, index: tenant.index })
      .pipe(
        take(1),
        finalize(() => this.reprovisionnementEnCours.set(false)),
      )
      .subscribe(succes => {
        if (succes) {
          this.verifierFilsDeLEau();
        }
      });
  }

  // Un partenaire actif mais non provisionné est une anomalie (le flux tournerait dans le
  // vide) ; non actif et non provisionné, c'est l'état normal d'un fil de l'eau non souscrit
  toneFilDeLEau(partenaire: EtatPartenaireFilDeLEau): StatusTone {
    if (partenaire.provisionne) {
      return 'success';
    }
    return partenaire.actif ? 'error' : 'muted';
  }

  libelleFilDeLEau(partenaire: EtatPartenaireFilDeLEau): string {
    return `${partenaire.partenaire} : ${partenaire.provisionne ? 'provisionné' : 'non provisionné'}`;
  }

  // Le détail des objets manquants, tel que rendu par le script de vérification du backend
  infobulleFilDeLEau(partenaire: EtatPartenaireFilDeLEau): string {
    if (partenaire.provisionne) {
      return partenaire.actif ? 'Base équipée et fil de l’eau activé' : 'Base équipée, fil de l’eau non activé';
    }

    const manquants = partenaire.manquants
      .map(manquant => (manquant.detail ? `${manquant.objet} (${manquant.detail})` : manquant.objet))
      .join(' ; ');
    return manquants ? `Objets manquants : ${manquants}` : 'Objets manquants non détaillés';
  }

  // Synchronisation complète (options + connection string) : le sens est demandé à
  // l'utilisateur avant de lancer quoi que ce soit
  resynchroniser() {
    const tenant = this.tenantSelectionne();
    if (!tenant) {
      return;
    }

    // Choix d'un sens, pas une saisie : `open` suffit, le fond peut refermer
    this.modal.open<SensSynchronisation>(DialogSensSynchronisationComponent).closed.subscribe(sens => {
      if (sens) {
        this.listeClientsService.clientAResynchroniser.set({
          identifiant: tenant.identifier,
          index: tenant.index,
          sens,
        });
      }
    });
  }

  // Divergence tenant/annuaire pour un champ donné (le tenant fait foi à l'affichage)
  desynchronisation(champ: string) {
    return this.detailClient()?.desynchronisations.find(desynchronisation => desynchronisation.champ === champ);
  }

  // Une clé Meetch est déjà enregistrée : sert à distinguer « pas de clé » de « clé
  // présente mais non affichée », le backend n'en renvoyant que la présence
  cleMeetchEnregistree = computed(() => this.detailClient()?.meetchApiKeyPresente ?? false);

  // Le Valider est verrouillé tant que le détail n'est pas chargé : détail absent (échec
  // de chargement compris — le service replie sur undefined), le formulaire ne porte que
  // les valeurs de la ligne de liste et enverrait label/disabled/isActiveArchiving à vide
  validationIndisponible = computed(() => this.chargementDetailEnCours() || !this.detailClient());

  // Reprise champ par champ de la valeur de l'annuaire. La clé Meetch en est exclue : sa
  // valeur ne quitte pas le serveur, la reprise passe par « Resynchroniser depuis l'annuaire »
  appliquerValeurAnnuaire(champ: string) {
    const desynchronisation = this.desynchronisation(champ);
    if (!desynchronisation) {
      return;
    }

    switch (champ) {
      case 'label':
        this.clientForm.controls.label.setValue(desynchronisation.valeurAnnuaire ?? '');
        break;
      case 'disabled':
        this.clientForm.controls.disabled.setValue(desynchronisation.valeurAnnuaire === 'true');
        break;
      case 'isActiveArchiving':
        this.clientForm.controls.isActiveArchiving.setValue(desynchronisation.valeurAnnuaire === 'true');
        break;
    }
  }

  modifierBaseDeDonnees() {
    const tenant = this.tenantSelectionne();
    const baseDeDonnees = this.clientForm.controls.databaseName.value;
    if (!tenant || !baseDeDonnees || this.chargementConnectionStringEnCours()) {
      return;
    }

    // Les informations du Key Vault ne sont chargées qu'ici : l'appel est trop lent
    // pour être fait à chaque sélection de client
    this.chargementConnectionStringEnCours.set(true);
    this.listeClientsService
      .recupererInformationsBaseDeDonnees$(baseDeDonnees)
      .pipe(
        take(1),
        finalize(() => this.chargementConnectionStringEnCours.set(false)),
      )
      .subscribe(informations => {
        const data: DialogModifierBaseDeDonneesData = {
          nomConnectionString: baseDeDonnees,
          informations,
          connexionAnnuaire: this.detailClient()?.connexionAnnuaire,
        };

        this.modal
          .openForm<DialogModifierBaseDeDonneesOutput, DialogModifierBaseDeDonneesData>(
            DialogModifierBaseDeDonneesComponent,
            { data },
          )
          .closed.subscribe(resultat => {
            if (resultat) {
              // Double écriture : secret Key Vault + fiche annuaire + App Config si renommage
              this.listeClientsService.connectionStringAMettreAJour.set({
                identifiantTenant: tenant.identifier,
                indexTenant: tenant.index,
                nomConnectionString: baseDeDonnees,
                ...resultat,
              });
            }
          });
      });
  }

  valider() {
    const tenant = this.tenantSelectionne();
    // Garde défensive en plus du [disabled] du bouton : le ngSubmit du formulaire (touche
    // Entrée) contournerait sinon le verrou visuel
    if (!tenant || this.validationIndisponible()) {
      return;
    }

    // Payload unique : le backend écrit le tenant (source de vérité) et reflète vers
    // l'annuaire legacy — un Valider réaligne donc les deux sources
    this.listeClientsService.clientAMettreAJour.set({
      index: tenant.index,
      identifier: tenant.identifier,
      databaseName: this.clientForm.controls.databaseName.value,
      managerIsEnabled: this.clientForm.controls.managerIsEnabled.value,
      meetchIsEnabled: this.clientForm.controls.meetchIsEnabled.value,
      isOrange: this.clientForm.controls.isOrange.value,
      isSkidata: this.clientForm.controls.isSkidata.value,
      isEventim: this.clientForm.controls.isEventim.value,
      label: this.clientForm.controls.label.value || undefined,
      disabled: this.clientForm.controls.disabled.value,
      isActiveArchiving: this.clientForm.controls.isActiveArchiving.value,
      // Meetch désactivé = clé API purgée (NULL annuaire, valeur vidée App Config)
      meetchApiKey: this.clientForm.controls.meetchIsEnabled.value
        ? this.clientForm.controls.meetchApiKey.value || undefined
        : undefined,
    });
  }
}
